import { useEffect, useMemo, useState } from 'react'
import type { Activation, Entitlement } from '../types'
import { Box, Laptop, RefreshCw, X } from './Icons'

type EntitlementPatch = {
  seat_count?: number
  seat_limit_enforced?: boolean
  updates_until?: string
  channel?: string
  status?: string
}

type Props = {
  entitlement: Entitlement
  activations: Activation[]
  busy: boolean
  canEdit?: boolean
  canDeactivate?: boolean
  canRevoke?: boolean
  onClose: () => void
  onDeactivate: (activationId: string) => Promise<void>
  onRevoke?: (activationId: string, reason: string) => Promise<void>
  onPatch: (patch: EntitlementPatch) => Promise<void>
  onRefresh: () => Promise<void>
}

function titleCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function relativeDate(value: string) {
  const minutes = Math.max(
    1,
    Math.round((Date.now() - new Date(value).getTime()) / 60_000),
  )
  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`
  return `${Math.round(minutes / 1440)}d ago`
}

export function Inspector({
  entitlement,
  activations,
  busy,
  canEdit = true,
  canDeactivate = true,
  canRevoke = false,
  onClose,
  onDeactivate,
  onRevoke,
  onPatch,
  onRefresh,
}: Props) {
  const [confirming, setConfirming] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [seatCount, setSeatCount] = useState(entitlement.seat_count)
  const [seatLimitEnforced, setSeatLimitEnforced] = useState(
    entitlement.seat_limit_enforced,
  )
  const [updatesUntil, setUpdatesUntil] = useState(entitlement.updates_until ?? '')
  const [channel, setChannel] = useState(entitlement.channel)
  const [status, setStatus] = useState(entitlement.status)

  useEffect(() => {
    setSeatCount(entitlement.seat_count)
    setSeatLimitEnforced(entitlement.seat_limit_enforced)
    setUpdatesUntil(entitlement.updates_until ?? '')
    setChannel(entitlement.channel)
    setStatus(entitlement.status)
    setConfirming(null)
  }, [entitlement])

  const visibleActivations = useMemo(
    () =>
      showHistory
        ? activations
        : activations.filter((activation) => activation.status === 'active'),
    [activations, showHistory],
  )
  const dirty =
    seatCount !== entitlement.seat_count ||
    seatLimitEnforced !== entitlement.seat_limit_enforced ||
    updatesUntil !== (entitlement.updates_until ?? '') ||
    channel !== entitlement.channel ||
    status !== entitlement.status

  const save = async () => {
    if (seatCount < Math.max(1, entitlement.active_seats)) return
    const patch: EntitlementPatch = {
      seat_count: seatCount,
      seat_limit_enforced: seatLimitEnforced,
      channel,
      status,
    }
    if (updatesUntil) patch.updates_until = updatesUntil
    await onPatch(patch)
  }

  return (
    <aside className="inspector" aria-label="Entitlement details">
      <header className="inspector-heading">
        <strong>Entitlement details</strong>
        <button type="button" onClick={onClose} aria-label="Close inspector">
          <X size={17} />
        </button>
      </header>
      <div className="customer-block">
        <span>Customer</span>
        <strong>{entitlement.customer_name}</strong>
        <p>{entitlement.email}</p>
      </div>
      <dl className="detail-list">
        <div>
          <dt>Entitlement ID</dt>
          <dd className="mono" title={entitlement.id}>
            {entitlement.id}
          </dd>
        </div>
        <div>
          <dt>Product</dt>
          <dd className="product-detail">
            <Box size={16} /> {entitlement.product}
          </dd>
        </div>
        <div>
          <dt>Plan</dt>
          <dd>{entitlement.plan}</dd>
        </div>
      </dl>

      <section className="entitlement-editor" aria-label="Edit entitlement">
        <div className="editor-heading">
          <strong>Access policy</strong>
          <span>{entitlement.active_seats} active</span>
        </div>
        <label>
          <span>Seat count</span>
          <input
            type="number"
            min={Math.max(1, entitlement.active_seats)}
            value={seatCount}
            disabled={busy || !canEdit || !seatLimitEnforced}
            onChange={(event) => setSeatCount(Number(event.target.value))}
          />
        </label>
        <label className="check-control">
          <input
            type="checkbox"
            checked={seatLimitEnforced}
            disabled={busy || !canEdit}
            onChange={(event) => setSeatLimitEnforced(event.target.checked)}
          />
          <span>Enforce seat limit</span>
        </label>
        <label>
          <span>Updates until</span>
          <input
            type="date"
            value={updatesUntil}
            disabled={busy || !canEdit}
            onChange={(event) => setUpdatesUntil(event.target.value)}
          />
        </label>
        <label>
          <span>Channel</span>
          <select
            value={channel}
            disabled={busy || !canEdit}
            onChange={(event) => setChannel(event.target.value)}
          >
            <option value="stable">Stable</option>
            <option value="beta">Beta</option>
            <option value="nightly">Nightly</option>
            <option value="internal">Internal</option>
          </select>
        </label>
        <label>
          <span>Status</span>
          <select
            value={status}
            disabled={busy || !canEdit}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="revoked">Revoked</option>
            <option value="expired">Expired</option>
          </select>
        </label>
        <button
          className="save-policy"
          type="button"
          disabled={
            busy ||
            !canEdit ||
            !dirty ||
            seatCount < Math.max(1, entitlement.active_seats)
          }
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </section>

      <section className="activation-list">
        <header>
          <strong>Activations</strong>
          <button type="button" disabled={busy} onClick={() => void onRefresh()}>
            <RefreshCw size={14} /> Refresh
          </button>
        </header>
        {visibleActivations.map((activation) => (
          <article key={activation.id}>
            <Laptop size={16} />
            <div>
              <strong>{activation.device_name}</strong>
              <span>
                {activation.platform} · {activation.host_app} {activation.host_version}
              </span>
              {showHistory && (
                <span className={`activation-state ${activation.status}`}>
                  {titleCase(activation.status)}
                </span>
              )}
            </div>
            <div className="last-seen">
              <span>{relativeDate(activation.last_seen_at)}</span>
              {canDeactivate && activation.status === 'active' &&
                (confirming === activation.id ? (
                  <span className="confirm-actions">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void onDeactivate(activation.id)}
                    >
                      Confirm
                    </button>
                    <button type="button" onClick={() => setConfirming(null)}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    className="danger-action"
                    type="button"
                    onClick={() => setConfirming(activation.id)}
                  >
                    Deactivate
                  </button>
                ))}
              {canRevoke && activation.status !== 'revoked' && onRevoke && (
                <button
                  className="danger-action"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const reason = window.prompt(
                      `Permanently revoke ${activation.device_name}? Enter an audit reason (8–500 characters). Do not include license keys or other secrets.`,
                    )?.trim()
                    if (reason) void onRevoke(activation.id, reason)
                  }}
                >
                  Revoke
                </button>
              )}
            </div>
          </article>
        ))}
        {visibleActivations.length === 0 && (
          <p className="activation-empty">No active devices.</p>
        )}
        <button
          className="view-all"
          type="button"
          onClick={() => setShowHistory((value) => !value)}
        >
          {showHistory ? 'Show active only' : 'View activation history'}
        </button>
      </section>
    </aside>
  )
}
